const WEIGHTS = {
  viewsAdded7d: 0.3,
  viewsGrowthPercent: 0.25,
  viewAcceleration: 0.2,
  videosAdded7d: 0.1,
  videosGrowthPercent: 0.15,
};

const asFiniteNumber = value => {
  if (value == null || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const quantile = (values, probability) => {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const position = (sorted.length - 1) * probability;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower);
};

const readWindowValue = (window, metric) => {
  if (window?.available !== true) return null;
  const value = asFiniteNumber(window[metric]);
  return value != null && value >= 0 ? value : null;
};

const readMetrics = (game, index, viewGrowthById, videoGrowthById) => {
  const oneDay = game?.window_1d;
  const sevenDays = game?.window_7d;
  if (oneDay?.available !== true || sevenDays?.available !== true) {
    return { game, index, eligible: false, reason: "missing-window" };
  }
  if (oneDay.partial !== false || sevenDays.partial !== false ||
      !Number.isFinite(oneDay.observed_dates) || oneDay.observed_dates < 2 ||
      !Number.isFinite(sevenDays.observed_dates) || sevenDays.observed_dates < 7) {
    return { game, index, eligible: false, reason: "partial-window" };
  }

  const currentViews = asFiniteNumber(game.views);
  const currentVideos = asFiniteNumber(game.videos);
  const viewsAdded1d = readWindowValue(oneDay, "views");
  const viewsAdded7d = readWindowValue(sevenDays, "views");
  const videosAdded1d = readWindowValue(oneDay, "videos");
  const videosAdded7d = readWindowValue(sevenDays, "videos");
  if ([currentViews, currentVideos, viewsAdded1d, viewsAdded7d, videosAdded1d, videosAdded7d]
    .some(value => value == null)) {
    return { game, index, eligible: false, reason: "invalid-window-metric" };
  }
  if (currentViews < 0 || currentVideos < 0 || viewsAdded7d <= 0) {
    return { game, index, eligible: false, reason: "no-valid-view-activity" };
  }

  const previousViews = currentViews - viewsAdded7d;
  const previousVideos = currentVideos - videosAdded7d;
  if (previousViews <= 0) {
    return { game, index, eligible: false, reason: "invalid-view-baseline" };
  }
  const trailingSixDayViews = viewsAdded7d - viewsAdded1d;
  const viewAcceleration = trailingSixDayViews > 0
    ? viewsAdded1d / (trailingSixDayViews / 6)
    : null;

  return {
    game,
    index,
    eligible: true,
    viewsAdded1d,
    viewsAdded7d,
    viewsGrowthPercent: viewGrowthById.get(game.selectionKey || game.gameKey || game.name) ?? null,
    previousViews,
    videosAdded1d,
    videosAdded7d,
    videosGrowthPercent: previousVideos > 0
      ? videoGrowthById.get(game.selectionKey || game.gameKey || game.name) ?? null
      : null,
    previousVideos,
    trailingSixDayViews,
    viewAcceleration,
    componentPercentiles: {},
    componentEligibility: {},
  };
};

const assignPercentiles = (rows, key, predicate = () => true) => {
  const ranked = rows
    .filter(row => row.eligible && predicate(row) && Number.isFinite(row[key]))
    .sort((a, b) => a[key] - b[key]);
  let start = 0;
  while (start < ranked.length) {
    let end = start + 1;
    while (end < ranked.length && ranked[end][key] === ranked[start][key]) end += 1;
    const percentile = ranked.length < 2
      ? 0
      : ((start + end - 1) / 2 / (ranked.length - 1)) * 100;
    for (let index = start; index < end; index += 1) {
      ranked[index].componentPercentiles[key] = percentile;
    }
    start = end;
  }
};

const makeComponent = (value, percentile, weight, included) => ({
  value,
  percentile: included ? percentile ?? 0 : 0,
  weight,
  weightedScore: included ? ((percentile ?? 0) * weight) : 0,
  included,
});

export function rankTikTokMomentum(games) {
  const sourceRows = Array.isArray(games) ? games : [];
  const viewGrowthById = new Map(tiktokMarketGames(sourceRows, "7d", "views", "views")
    .map(row => [row.id, row.growthPT * 100]));
  const videoGrowthById = new Map(tiktokMarketGames(sourceRows, "7d", "views", "videos")
    .map(row => [row.id, row.growthPT * 100]));
  const metrics = sourceRows.map((game, index) =>
    readMetrics(game, index, viewGrowthById, videoGrowthById));
  const eligible = metrics.filter(row => row.eligible);
  const thresholds = {
    minViewsAddedForGrowth: quantile(eligible.map(row => row.viewsAdded7d), 0.25),
    minPreviousViewsForGrowth: quantile(eligible.map(row => row.previousViews), 0.25),
    minTrailingSixDayViewsForAcceleration: quantile(
      eligible.map(row => row.trailingSixDayViews).filter(value => value > 0),
      0.25
    ),
    minVideosAddedForGrowth: quantile(
      eligible.map(row => row.videosAdded7d).filter(value => value > 0),
      0.25
    ),
    minPreviousVideosForGrowth: quantile(
      eligible.map(row => row.previousVideos).filter(value => value > 0),
      0.25
    ),
  };

  for (const row of eligible) {
    row.componentEligibility = {
      viewsAdded7d: true,
      viewsGrowthPercent: row.viewsAdded7d >= thresholds.minViewsAddedForGrowth &&
        row.previousViews >= thresholds.minPreviousViewsForGrowth &&
        Number.isFinite(row.viewsGrowthPercent),
      viewAcceleration: row.trailingSixDayViews >= thresholds.minTrailingSixDayViewsForAcceleration &&
        row.viewAcceleration != null,
      videosAdded7d: true,
      videosGrowthPercent: row.viewsAdded7d >= thresholds.minViewsAddedForGrowth &&
        row.videosAdded7d >= thresholds.minVideosAddedForGrowth &&
        row.previousVideos >= thresholds.minPreviousVideosForGrowth &&
        row.videosGrowthPercent != null,
    };
  }

  assignPercentiles(eligible, "viewsAdded7d");
  assignPercentiles(eligible, "viewsGrowthPercent", row => row.componentEligibility.viewsGrowthPercent);
  assignPercentiles(eligible, "viewAcceleration", row => row.componentEligibility.viewAcceleration);
  assignPercentiles(eligible, "videosAdded7d");
  assignPercentiles(eligible, "videosGrowthPercent", row => row.componentEligibility.videosGrowthPercent);

  const scored = eligible.map(row => {
    const components = {
      viewsAdded7d: makeComponent(row.viewsAdded7d, row.componentPercentiles.viewsAdded7d,
        WEIGHTS.viewsAdded7d, row.componentEligibility.viewsAdded7d),
      viewsGrowthPercent: makeComponent(row.viewsGrowthPercent, row.componentPercentiles.viewsGrowthPercent,
        WEIGHTS.viewsGrowthPercent, row.componentEligibility.viewsGrowthPercent),
      viewAcceleration: makeComponent(row.viewAcceleration, row.componentPercentiles.viewAcceleration,
        WEIGHTS.viewAcceleration, row.componentEligibility.viewAcceleration),
      videosAdded7d: makeComponent(row.videosAdded7d, row.componentPercentiles.videosAdded7d,
        WEIGHTS.videosAdded7d, row.componentEligibility.videosAdded7d),
      videosGrowthPercent: makeComponent(row.videosGrowthPercent, row.componentPercentiles.videosGrowthPercent,
        WEIGHTS.videosGrowthPercent, row.componentEligibility.videosGrowthPercent),
    };
    const score = Object.values(components).reduce((total, component) => total + component.weightedScore, 0);
    return { ...row, components, score };
  }).sort((a, b) => b.score - a.score ||
    b.viewsAdded7d - a.viewsAdded7d ||
    b.videosAdded7d - a.videosAdded7d ||
    Number(a.game.config_id || 0) - Number(b.game.config_id || 0) ||
    String(a.game.selectionKey || a.game.id || "").localeCompare(String(b.game.selectionKey || b.game.id || "")));

  const rankByIndex = new Map(scored.map((row, rank) => [row.index, rank + 1]));
  const scoreByIndex = new Map(scored.map(row => [row.index, row]));
  const strongRankCutoff = Math.max(1, Math.ceil(scored.length * 0.05));
  const rows = metrics.map(row => {
    if (!row.eligible) {
      return {
        ...row.game,
        momentumEligible: false,
        momentumIneligibleReason: row.reason,
        momentumRank: null,
        momentumScore: null,
        momentumMetrics: null,
        momentumComponents: null,
        momentumStrong: false,
      };
    }
    const result = scoreByIndex.get(row.index);
    return {
      ...row.game,
      momentumEligible: true,
      momentumIneligibleReason: null,
      momentumRank: rankByIndex.get(row.index),
      momentumScore: result.score,
      momentumMetrics: {
        viewsAdded1d: row.viewsAdded1d,
        viewsAdded7d: row.viewsAdded7d,
        viewsGrowthPercent: row.viewsGrowthPercent,
        previousViews: row.previousViews,
        videosAdded1d: row.videosAdded1d,
        videosAdded7d: row.videosAdded7d,
        videosGrowthPercent: row.videosGrowthPercent,
        previousVideos: row.previousVideos,
        trailingSixDayViews: row.trailingSixDayViews,
        viewAcceleration: row.viewAcceleration,
      },
      momentumComponents: result.components,
      momentumStrong: rankByIndex.get(row.index) <= strongRankCutoff,
    };
  });

  return {
    rows,
    eligibleCount: eligible.length,
    thresholds,
    strongRankCutoff,
  };
}

export const TIKTOK_MOMENTUM_WEIGHTS = WEIGHTS;
import { tiktokMarketGames } from "./tiktokMarket";
